import { useEffect, useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IPersonalSiteFilter, ITableColumn, IWorkspaceFilter } from "@interfaces/common.interfaces";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import {
    IInactiveWorkspacesInTenantReportDetails,
    IInactiveWorkspacesInTenantReportItem,
    IInactiveWorkspacesInTenantReportRequestModel
} from "@interfaces/reports/inactive-workspaces-reports.interfaces";
import InactiveWorkspacesInTenantReportSummary from "components/reports/usage-reports/inactive-workspaces-in-tenant-report/inactive-workspaces-in-tenant-report-summary";
import PersonalSiteFilter from "components/controls/personal-site-filter/personal-site-filter";
import MinMaxInput from "components/common/inputs/min-max-input";
import WorkspaceLinkCell from "components/reports-common/report-results-grid-cells/workspace-link-cell/workspace-link-cell";
import PrincipalsCell from "components/reports-common/report-results-grid-cells/principals-cell/principals-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import { getWorkspaceTypeKey } from "lib/workspaces";
import { escapeForExport } from "lib/utils";
import WorkspaceFilter from "components/controls/workspace-filter/workspace-filter";
import { isWorkspaceFilterValid } from "lib/request-model-validation-helper";

const reportUrl: string = "/api/reports/inactive-workspaces-in-tenant";

export default function InactiveWorkspacesInTenantReport() {
    const { t } = useL10n<ILocaleStrings>();

    const [model, setModel] = useState<IInactiveWorkspacesInTenantReportRequestModel>({
        inactivityDays: 30
    });
    const [inactivityDays, setInactivityDays] = useState<number>(30);
    const [personalSiteFilter, setPersonalSiteFilter] = useState<IPersonalSiteFilter>({
        includeWorkspaces: true,
        includeOneDrive: false
    });
    const [workspaceFilter, setWorkspaceFilter] = useState<IWorkspaceFilter>({
        applyFilter: false
    });

    // Define table columns
    const cols: Array<ITableColumn<IInactiveWorkspacesInTenantReportItem>> = [
        {
            key: "workspaceName",
            label: t("ppaWorkspace"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IInactiveWorkspacesInTenantReportItem) => (
                <WorkspaceLinkCell
                    name={item.workspaceName}
                    url={item.workspaceUrl}
                    template={item.workspaceTemplate}
                    type={item.workspaceType}
                />
            )
        },
        {
            key: "workspaceUrl",
            label: t("ppaWorkspaceUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "workspaceType",
            label: t("ppaWorkspaceType"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IInactiveWorkspacesInTenantReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            ),
            getExportValue: (key: string, item: IInactiveWorkspacesInTenantReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            )
        },
        {
            key: "workspaceTemplate",
            label: t("ppaWorkspaceTemplate"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "owners",
            label: t("ppaOwners"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IInactiveWorkspacesInTenantReportItem) => (
                <PrincipalsCell columnId={key} item={item} />
            ),
            getExportValue: (key: string, item: IInactiveWorkspacesInTenantReportItem) => {
                return escapeForExport((item as any)[key]);
            }
        },
        {
            key: "owners",
            label: t("ppaOwnerUpns"),
            showInGrid: false,
            showInExport: true,
            getExportValue: (key: string, item: IInactiveWorkspacesInTenantReportItem) => {
                return escapeForExport((item as any)[key].map((o: any) => o.upn));
            }
        },
        {
            key: "accessCount",
            label: t("ppaAccessCount"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 120
        },
        {
            key: "userVisits",
            label: t("ppaUserVisits"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 120
        },
        {
            key: "lastModified",
            label: t("lastModified"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IInactiveWorkspacesInTenantReportItem) => (
                <DateTimeCell columnId={key} item={item} pattern="PPp" />
            )
        }
    ];

    const onInactivityDaysChange = (value: number) => {
        setInactivityDays(value);
        setModel({
            ...model,
            inactivityDays: value
        });
    };

    const onPersonalSiteFilterChange = (value?: IPersonalSiteFilter) => {
        setPersonalSiteFilter({
            includeWorkspaces: value?.includeWorkspaces ?? false,
            includeOneDrive: value?.includeOneDrive ?? false
        });
        setModel({
            ...model,
            personalSiteFilter: {
                includeWorkspaces: value?.includeWorkspaces,
                includeOneDrive: value?.includeOneDrive
            }
        });
    };

    const onWorkspaceFilterChange = (value?: IWorkspaceFilter) => {
        setWorkspaceFilter({
            applyFilter: value?.applyFilter || false,
            filter: value?.filter
        });
        setModel({
            ...model,
            workspaceFilter: value?.applyFilter ?
                {
                    property: value?.filter?.property,
                    operator: value?.filter?.operator,
                    value: value?.filter?.value
                }
                : undefined
        });
    };

    // Model is valid if at least one toggle is true and inactivityDays is between 1 and 90
    const isModelValid = (): boolean => {
        const hasPersonalSiteFilter = (model.personalSiteFilter?.includeWorkspaces || model.personalSiteFilter?.includeOneDrive) === true;
        const hasValidDays = inactivityDays > 0 && inactivityDays <= 90;

        return hasPersonalSiteFilter && hasValidDays && isWorkspaceFilterValid(workspaceFilter.applyFilter, model.workspaceFilter);
    };

    const reportSectionProps: IReportSectionProps<IInactiveWorkspacesInTenantReportItem, IInactiveWorkspacesInTenantReportDetails> = {
        columns: cols,
        summaryComponent: <InactiveWorkspacesInTenantReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaInactiveWorkspacesInTenantReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaInactiveWorkspacesInTenantReportDefaultExportFilenamePrefix"
    }

    return (
        <ReportSection {...reportSectionProps}>
            <MinMaxInput
                value={inactivityDays}
                min={1}
                max={90}
                label={t("ppaInactivityDays")}
                tooltip={t("ppaInactivityDaysTooltip")}
                required={true}
                onChange={onInactivityDaysChange}
            />
            <WorkspaceFilter model={workspaceFilter} onChange={onWorkspaceFilterChange} />
            <PersonalSiteFilter model={personalSiteFilter} onChange={onPersonalSiteFilterChange} />
        </ReportSection>
    );
}
